import React, { useEffect, useState } from 'react';
import type { Pt } from '../../utils/projection';
import { getCropPreview } from '../../api/frames';

interface BrightenedCropPreviewProps {
  frameId: number;
  /** 4 normalized points, TL,TR,BR,BL. */
  pointsNorm: Pt[];
  perspectiveWidth: number;
  perspectiveHeight: number;
  /** Table.crop_brighten, 0..1. */
  strength: number;
  boxSize?: number;
}

/**
 * «Предпросмотр кропа» с осветлением стола (Алексей 26.10, #7755/#7770). CLAHE в браузере не повторить:
 * кадр без CORS, пиксели canvas не читаются (см. CropPreview). Кроп режет бэкенд тем же кодом, что
 * пайплайн (transform_single_area + brighten_crop), сюда приходит готовый JPEG.
 */
const BrightenedCropPreview: React.FC<BrightenedCropPreviewProps> = ({
  frameId, pointsNorm, perspectiveWidth, perspectiveHeight, strength, boxSize = 240,
}) => {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!perspectiveWidth || !perspectiveHeight || pointsNorm.length !== 4) return;
    let url: string | null = null;
    let cancelled = false;
    // Ползунок тянут — не дёргать бэкенд на каждый шаг.
    const timer = window.setTimeout(() => {
      getCropPreview({
        frame_id: frameId,
        points: pointsNorm.flat().map(v => v.toFixed(5)).join(','),
        width: perspectiveWidth,
        height: perspectiveHeight,
        brighten: strength,
      })
        .then(res => {
          if (cancelled) return;
          url = URL.createObjectURL(res.data);
          setSrc(url);
          setFailed(false);
        })
        .catch(() => { if (!cancelled) setFailed(true); });
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      if (url) URL.revokeObjectURL(url);
    };
  }, [frameId, pointsNorm, perspectiveWidth, perspectiveHeight, strength]);

  return (
    <div
      className="flex items-center justify-center rounded border border-border bg-black"
      style={{ width: boxSize, height: boxSize }}
    >
      {src && !failed
        ? <img src={src} alt="" className="max-h-full max-w-full object-contain" />
        : <span className="text-[11px] text-muted-foreground">{failed ? '—' : '…'}</span>}
    </div>
  );
};

export default BrightenedCropPreview;
